import { PlusOutlined, SyncOutlined } from "@ant-design/icons";
import { App, Button, Divider, Drawer, Flex, Form, Masonry, Radio, Typography } from "antd";
import { useCallback, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { ScriptEditForm, type FormData } from "./components/ScriptEditForm";
import { useUserClaims } from "@src/frontend/hooks/useUserClaims";
import { ActionItem } from "./components/ActionItem";
import { useScriptsAPI } from "./hooks/useActionAPI";
import type { UserScriptModel } from "@src/generated/prisma/models";
import { ResourceQuickAdd, type QuickAddRef } from "./components/ResourceQuickAdd";

const StyledComponent = styled(Flex)``;

export const ActionsPage = () => {
  const [openType, setOpenType] = useState<OpenType>(OpenType.Pub);
  const [editOpen, setEditOpen] = useState(false);
  const [form] = Form.useForm<UserScriptModel>();
  const [claims] = useUserClaims();
  const [editForm, setEditForm] = useState<UserScriptModel>();
  const { message } = App.useApp();
  const { createReq, listPubReq, listReq, delReq, updateReq } = useScriptsAPI();
  const handleSubmit = useCallback(async (data: FormData, id?: string) => {
    console.debug('submit', { data, id });
    const success = await (id ? updateReq.runAsync(data, id) : createReq.runAsync(data))
      .then(data => data.ok)
      .catch(() => false);
    await Promise.all([
      listReq.runAsync(),
      listPubReq.runAsync(),
    ]);
    if (success) setEditOpen(false);
    setOpenType(OpenType.Pri);
    if (!id) {
      message.success('已创建');
    } else {
      message.success('已更新');
    }
    return success;
  }, [createReq, listReq]);
  const viewSource = useMemo(() => {
    if (openType === OpenType.Pri) return listReq.data?.data ?? [];
    return listPubReq.data?.data ?? [];
  }, [openType, listPubReq, listReq]);
  const handleClick = useCallback((e: UserScriptModel) => {
    setEditForm(e);
    setEditOpen(true);
    form.setFieldsValue(e);
  }, []);
  const handleDelete = useCallback(async (e: UserScriptModel) => {
    await delReq.runAsync(e.id);
    await Promise.all([
      listReq.runAsync(),
      listReq.runAsync(),
    ]);
  }, []);
  const isMine = useMemo(() => {
    if (!editForm) true;
    return claims?.sub === editForm?.logto_uid;
  }, [editForm, claims]);

  const quickAddRef = useRef<QuickAddRef>(null);

  return (
    <StyledComponent className="page-shell" vertical gap={12}>
      <Flex className="page-header" vertical>
        <Typography.Text className="page-eyebrow">AUTOMATION</Typography.Text>
        <Typography.Title level={2} className="page-title">自动化脚本</Typography.Title>
        <Typography.Text type="secondary">编排、共享并复用你的浏览器操作流程。</Typography.Text>
      </Flex>
      <Flex gap={12} className="btn-container">
        <Button
          block
          size="large"
          type={!listReq.data?.data?.length ? `primary` : 'default'}
          onClick={() => {
            form.resetFields();
            setEditOpen(true);
            setEditForm(undefined);
          }}
          icon={<PlusOutlined />}
        >创建 Action</Button>
        <Button size="large" onClick={async () => {
          await Promise.all([listPubReq.runAsync(), listReq.runAsync()]);
        }}><SyncOutlined spin={listReq.loading || listPubReq.loading} /></Button>
      </Flex>
      <Flex gap={24} justify="center" style={{ marginTop: 24 }}>
        <Radio.Group
          optionType="button"
          value={openType}
          onChange={v => setOpenType(v.target.value)}
          options={[
            { label: OpenType.Pub, value: OpenType.Pub },
            { label: OpenType.Pri, value: OpenType.Pri },
          ]}
        />
      </Flex>
      <Divider>共 {viewSource.length} 个</Divider>
      <Masonry
        items={viewSource.map(e => ({ data: e, key: e.id }))}
        columns={{
          xxxl: 8,
          xxl: 6,
          xl: 4,
          lg: 3,
          md: 2,
          sm: 2
        }}
        gutter={24}
        itemRender={({ data: e, key }) => (
          <ActionItem
            key={key}
            script={e}
            onClick={handleClick}
            onDelete={handleDelete}
            onQuickAddClick={quickAddRef.current?.handleOpenModal}
            onCopy={async data => {
              await createReq.runAsync(data);
              await listReq.runAsync();
              message.success(`已复制：${data.name}`);
            }}
          />
        )}
      />
      <ResourceQuickAdd ref={quickAddRef} />
      <Drawer
        forceRender
        title={editForm ? `${editForm?.name}${isMine ? '' : '(查看)'}` : '创建新脚本'}
        mask={{ closable: editForm ? !isMine : false }}
        size={600}
        open={editOpen}
        onClose={async () => {
          setEditOpen(false);
          await new Promise(r => setTimeout(r, 300));
          setEditForm(undefined);
        }}>
        <ScriptEditForm
          isOwner={!editForm || isMine}
          form={form}
          onSubmit={data => handleSubmit(data, isMine ? editForm?.id : undefined)}
        />
      </Drawer>
    </StyledComponent>
  );
}

enum OpenType {
  Pub = '共享',
  Pri = '私有',
}
